import { useTranslation } from 'react-i18next';
import { useApprovalFlows } from '@/accessControl/hooks/useApprovalFlows.hooks';
import { useApprovalFlowsEnabled } from '@/accessControl/hooks/useApprovalFlowsEnabled.hook';
import { sqReportStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { isApprovalFlowsForOrganizerTopicsEnabled } from '@/services/systemConfiguration.utilities';
import { isPresentationWorkbookMode } from '@/utilities/workbookMode.utilities';
import { useWorkbookIsTopic } from '@/workbook/WorkbookIsTopic.context';
import {
  GetReportProductionStatusQueryKey,
  useApiGetReportProductionStatusQuery,
} from '@seeqdev/api/endpoint/ContentApi';
import type { TFunction } from 'i18next';
import type { ReportProductionSourceOutputV1 } from '@seeqdev/api/model/ReportProductionSourceOutputV1';

/**
 * Why a source is flagged. Both are read off its version history, and they differ in what can be done:
 * DIFFERENT_VERSION has a production version to move the content to, NONE_IN_PRODUCTION has nothing to point at.
 */
export type NonProductionReason = 'DIFFERENT_VERSION' | 'NONE_IN_PRODUCTION';

export type NonProductionSource = {
  id: string;
  name: string;
  /** Name of the source workbook's owner. Empty when the owner cannot be read. */
  ownerName: string;
  reason: NonProductionReason;
  /**
   * One piece of this source's content in the Topic, used to link to the source. The content API's sourceUrl
   * redirect is keyed by content, not by workbook, so a source showing several pieces of content is represented by
   * the first of them; they all resolve to the same source workbook.
   */
  contentId: string | undefined;
  /** How many pieces of this document's content come from the source. */
  contentCount: number;
};

export type NonProductionContentSources = {
  /**
   * Source workbooks that are not the production version, including ones the viewer cannot read, each with the
   * number of pieces of content it contributes.
   */
  notInProduction: NonProductionSource[];
  /** How many pieces of content sit on those sources in total. */
  affectedContentCount: number;
  /** True while the check is in flight, so callers can hold the banner back rather than flash it. */
  isChecking: boolean;
};

const NOTHING_TO_REPORT: NonProductionContentSources = {
  notInProduction: [],
  affectedContentCount: 0,
  isChecking: false,
};

/**
 * Identity of one document's content. Sorted so reordering does not look like an edit. Callers pass
 * `contentNotArchived`, so archiving a piece — how deletion is stored — changes the key.
 */
const documentContentKey = (content: { id: string }[]): string =>
  content
    .map((item) => item.id)
    .sort()
    .join('\0');

/**
 * Reports content from source workbooks that are not in production in two cases: the Topic is itself in production,
 * or an approval flow is attached, starting from the first step. A Topic with no flow attached has made no production
 * promise, and is left unreported.
 *
 * Scoped to the document being viewed, not the whole Topic. The status comes from
 * `GET /content/report/{reportId}/production-status`, which classifies every embedded source without requiring the
 * viewer to read that source. A source the viewer cannot read is still listed: its name is omitted, and its content
 * still counts. The owner's name is included for every source whose owner the viewer can read. Asking version
 * history as the viewer cannot do this, because a history the viewer cannot read is
 * filtered out rather than reported as stale.
 *
 * The request itself only names the report, so the generated cache key would stay put when content is added or
 * removed. The document's content ids are part of the key for that reason: a deletion has to discard the previous
 * classification. Otherwise the banner keeps listing what was removed, and the final approval step — which refuses
 * while `affectedContentCount` is non-zero — blocks on content that is no longer in the document.
 */
export const useNonProductionContentSources = (): NonProductionContentSources => {
  const status = useNonProductionStatus(useWorkbookIsTopic());

  if (status.isChecking || !status.sources) {
    return { ...NOTHING_TO_REPORT, isChecking: status.isChecking };
  }
  const { sources } = status;

  return {
    notInProduction: sources.map((source) => ({
      id: source.id,
      name: source.name ?? '',
      ownerName: source.ownerName ?? '',
      reason: toReason(source),
      contentId: source.contentIds[0],
      contentCount: source.contentIds.length,
    })),
    affectedContentCount: sources.reduce((count, source) => count + source.contentIds.length, 0),
    isChecking: false,
  };
};

export type NonProductionContentSource = {
  reason: NonProductionReason;
  /** The source's display name: its own name, or a restricted label numbered the same way as the banner. */
  label: string;
};

/**
 * Restricted sources have no name to tell them apart, so when there are several they are numbered in list order,
 * skipping readable sources. A single restricted source is left unnumbered.
 */
export const nonProductionSourceLabel = (
  t: TFunction,
  sources: { id: string; name?: string }[],
  { id, name }: { id: string; name?: string },
): string => {
  if (name) {
    return name;
  }
  const restrictedIds = sources.filter((source) => !source.name).map((source) => source.id);
  return restrictedIds.length > 1
    ? t('APPROVAL_FLOWS.NON_PRODUCTION_CONTENT.RESTRICTED_ANALYSIS_NUMBERED', { number: restrictedIds.indexOf(id) + 1 })
    : t('APPROVAL_FLOWS.NON_PRODUCTION_CONTENT.RESTRICTED_ANALYSIS');
};

/**
 * The source of one piece of content in the document being viewed, when that source is not in production; undefined
 * otherwise. Shares the production-status query, and its cache entry, with useNonProductionContentSources.
 *
 * Content renders in detached React roots, outside WorkbookIsTopicProvider, so the Topic flag is read from the
 * workbook store rather than from context.
 */
export const useNonProductionContentSource = (contentId: string): NonProductionContentSource | undefined => {
  const { t } = useTranslation();
  const isTopic = useFluxPath(sqWorkbookStore, (store) => store.isTopic);
  const { sources } = useNonProductionStatus(isTopic);
  const source = sources?.find(({ contentIds }) => contentIds.includes(contentId));
  return source && sources
    ? { reason: toReason(source), label: nonProductionSourceLabel(t, sources, source) }
    : undefined;
};

const toReason = (source: ReportProductionSourceOutputV1): NonProductionReason =>
  source.status === 'NONE_IN_PRODUCTION' ? 'NONE_IN_PRODUCTION' : 'DIFFERENT_VERSION';

/** Sources is undefined when there is nothing to report: suppressed, still checking, or no production promise. */
const useNonProductionStatus = (
  isTopic: boolean,
): { sources: ReportProductionSourceOutputV1[] | undefined; isChecking: boolean } => {
  const reportId = useFluxPath(sqReportStore, (store) => store.id);
  const documentContentIds = useFluxPath(sqReportStore, (store) => documentContentKey(store.contentNotArchived));
  const { hasApprovalFlowAttached } = useApprovalFlows();

  // Both flags are required, matching how a Topic reaches an approval flow in the first place: Organizer Topics ride
  // on the base feature, behind their own flag on top of it.
  const approvalFlowsEnabledForTopics = useApprovalFlowsEnabled() && isApprovalFlowsForOrganizerTopicsEnabled();
  // Suppressed for presentation mode and headless capture: both produce output for an audience or a PDF, where an
  // authoring warning is noise rather than information.
  const suppressed = !isTopic || !approvalFlowsEnabledForTopics || headlessRenderMode() || isPresentationWorkbookMode();

  const reportIdForQuery = reportId ?? '';
  // The generated key stops at the report id. Options are applied after that key, so this extends it with the
  // document's content. A new set of ids is a cache miss: the previous classification is not reused.
  const productionStatusQuery = {
    enabled: !suppressed && Boolean(reportId),
    queryKey: [...GetReportProductionStatusQueryKey({ reportId: reportIdForQuery }), documentContentIds],
  };
  const { data, isLoading } = useApiGetReportProductionStatusQuery(
    { reportId: reportIdForQuery },
    productionStatusQuery,
  );

  if (suppressed || !reportId) {
    return { sources: undefined, isChecking: false };
  }

  // Only the first load withholds the banner. isFetching also covers background refetches, and using it here would
  // tear the banner down and put it back on every window refocus, shifting the document under the reader.
  if (isLoading || !data) {
    return { sources: undefined, isChecking: true };
  }

  if (!data.topicInProduction && !hasApprovalFlowAttached) {
    return { sources: undefined, isChecking: false };
  }

  return { sources: data.sources, isChecking: false };
};
